import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "../contexts/AuthContext";
import { Button } from "../components/ui/button";
import { Card, CardContent } from "../components/ui/card";
import { Input } from "../components/ui/input";
import { ArrowLeft, Crown, Check, Sparkles, Star, BookOpen, Music, Heart, Dumbbell, Eye, Zap, Gift, Percent, Ticket, PartyPopper, MessageSquare, Infinity, Users, Leaf, Slider, Target, Trophy, TrendingUp, Send, Play } from "lucide-react";
import axios from "axios";
import { useState, useEffect } from "react";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "../components/ui/dialog";
import { Textarea } from "../components/ui/textarea";
import MatrixUnlock from "../components/MatrixUnlock";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Community Milestones Component
const CommunityMilestones = ({ milestones }) => {
  const defaultMilestones = milestones || [
    { target: 1000, current: 847, reward: "5 New Exclusive Sound Baths", icon: "🎵", unlocked: false },
    { target: 5000, current: 847, reward: "Manifestival Retreat Event Discount", icon: "🎪", unlocked: false },
    { target: 10000, current: 847, reward: "Exclusive Lala Land Brand Merch Discount (House of Racha Collab)", icon: "🏷️", unlocked: false },
    { target: 25000, current: 847, reward: "Free Guided Meditation Pack for ALL Members", icon: "🧘🏾", unlocked: false },
  ];

  return (
    <Card className="bg-gradient-to-br from-indigo-500/10 via-purple-500/10 to-pink-500/10 border-indigo-500/20 mb-8">
      <CardContent className="p-6">
        <div className="flex items-center gap-3 mb-4">
          <div className="w-12 h-12 rounded-full bg-gradient-to-br from-indigo-500 to-purple-500 flex items-center justify-center">
            <Target className="h-6 w-6 text-white" />
          </div>
          <div>
            <h3 className="font-bold text-lg text-primary">Community Milestones</h3>
            <p className="text-sm text-muted-foreground">When we grow together, everyone wins!</p>
          </div>
        </div>
        
        <div className="space-y-4">
          {defaultMilestones.map((milestone, index) => {
            const progress = Math.min((milestone.current / milestone.target) * 100, 100);
            const isUnlocked = milestone.current >= milestone.target;
            
            return (
              <div key={index} className={`p-4 rounded-xl ${isUnlocked ? 'bg-green-500/20 border border-green-500/30' : 'bg-background/50'}`}>
                <div className="flex items-center justify-between mb-2">
                  <div className="flex items-center gap-2">
                    <span className="text-2xl">{milestone.icon}</span>
                    <div>
                      <p className="font-medium text-primary text-sm">{milestone.reward}</p>
                      <p className="text-xs text-muted-foreground">
                        {isUnlocked ? '✅ UNLOCKED!' : `${milestone.current.toLocaleString()} / ${milestone.target.toLocaleString()} users`}
                      </p>
                    </div>
                  </div>
                  {isUnlocked && <Trophy className="h-5 w-5 text-green-500" />}
                </div>
                <div className="w-full bg-muted/50 rounded-full h-2">
                  <div 
                    className={`h-2 rounded-full transition-all duration-500 ${isUnlocked ? 'bg-green-500' : 'bg-gradient-to-r from-indigo-500 to-purple-500'}`}
                    style={{ width: `${progress}%` }}
                  />
                </div>
              </div>
            );
          })}
        </div>
        
        <p className="text-xs text-center text-muted-foreground mt-4 italic">
          Share Lala Land to help us reach these milestones faster! 🌱
        </p>
      </CardContent>
    </Card>
  );
};

// Pay What You Can Component
const PayWhatYouCan = ({ user, getAuthHeader, onSubscribe }) => {
  const navigate = useNavigate();
  const [amount, setAmount] = useState(5.55);
  const [loading, setLoading] = useState(false);
  const minAmount = 2.22;  // Minimum is hardship rate
  const maxAmount = 11.11;
  const suggestedAmount = 5.55;

  const getSliderLabel = () => {
    if (amount <= 2.22) return "Hardship Rate 💜";
    if (amount < 5.55) return "Community Supporter 🤝🏾";
    if (amount === 5.55) return "Suggested Amount ✨";
    if (amount < 8.88) return "Generous Soul 💫";
    return "Angel Supporter 👼🏾";
  };

  const handleSubscribe = async () => {
    if (!user) {
      // Redirect to register with return URL
      toast.info("Let's create your account first!");
      navigate('/register?redirect=/subscription&plan=pwc&amount=' + amount);
      return;
    }
    
    setLoading(true);
    try {
      const response = await axios.post(`${API}/subscription/pay-what-you-can`, {
        amount: amount,
        origin_url: window.location.origin
      }, { headers: getAuthHeader() });
      
      window.location.href = response.data.checkout_url;
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to process. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <Card className="bg-gradient-to-br from-rose-500/10 via-pink-500/10 to-purple-500/10 border-rose-500/20 mb-8">
      <CardContent className="p-6">
        <div className="flex items-center gap-3 mb-4">
          <div className="w-12 h-12 rounded-full bg-gradient-to-br from-rose-500 to-pink-500 flex items-center justify-center">
            <Heart className="h-6 w-6 text-white" />
          </div>
          <div>
            <h3 className="font-bold text-lg text-primary">Pay What You Can</h3>
            <p className="text-sm text-muted-foreground">Sliding scale for premium access</p>
          </div>
        </div>
        
        <div className="space-y-4">
          <div className="text-center">
            <p className="text-4xl font-bold text-primary mb-1">
              ${amount.toFixed(2)}<span className="text-lg text-muted-foreground">/month</span>
            </p>
            <p className="text-sm font-medium text-rose-500">{getSliderLabel()}</p>
          </div>
          
          <div className="px-2">
            <input
              type="range"
              min={minAmount}
              max={maxAmount}
              step={0.01}
              value={amount}
              onChange={(e) => setAmount(parseFloat(e.target.value))}
              className="w-full h-3 bg-gradient-to-r from-purple-400 via-pink-500 to-amber-500 rounded-full appearance-none cursor-pointer"
              style={{
                background: `linear-gradient(to right, #8b5cf6 0%, #ec4899 50%, #f59e0b 100%)`
              }}
            />
            <div className="flex justify-between text-xs text-muted-foreground mt-1">
              <span>$2.22 (Min)</span>
              <span>$5.55 (Suggested)</span>
              <span>$11.11</span>
            </div>
          </div>
          
          <div className="grid grid-cols-4 gap-2">
            {[2.22, 4.44, 5.55, 8.88].map((preset) => (
              <Button
                key={preset}
                variant={amount === preset ? "default" : "outline"}
                size="sm"
                onClick={() => setAmount(preset)}
                className="text-xs"
              >
                ${preset}
              </Button>
            ))}
          </div>
          
          <Button
            onClick={handleSubscribe}
            disabled={loading}
            className="w-full bg-gradient-to-r from-rose-500 to-pink-500 hover:from-rose-600 hover:to-pink-600 text-white"
          >
            {loading ? "Processing..." : `Subscribe for $${amount.toFixed(2)}/mo`}
          </Button>
          
          <p className="text-xs text-center text-muted-foreground">
            Those who pay more help subsidize those who can't. Thank you! 💜
          </p>
        </div>
      </CardContent>
    </Card>
  );
};

// Gift a Subscription Component
const GiftSubscription = ({ user, getAuthHeader }) => {
  const navigate = useNavigate();
  const [isOpen, setIsOpen] = useState(false);
  const [giftData, setGiftData] = useState({
    recipient_email: "",
    recipient_name: "",
    message: "",
    tier: "rooted",
    months: 1
  });
  const [loading, setLoading] = useState(false);

  const giftPricing = {
    1: { price: 5.55, label: "1 Month" },
    3: { price: 14.99, label: "3 Months", savings: "10%" },
    6: { price: 27.77, label: "6 Months", savings: "17%" },
    12: { price: 44.44, label: "1 Year", savings: "33%" }
  };

  const handleGift = async () => {
    if (!user) {
      toast.info("Let's create your account first!");
      navigate('/register?redirect=/subscription');
      return;
    }
    
    if (!giftData.recipient_email) {
      toast.error("Please enter recipient's email");
      return;
    }
    
    setLoading(true);
    try {
      const response = await axios.post(`${API}/subscription/gift`, {
        ...giftData,
        origin_url: window.location.origin
      }, { headers: getAuthHeader() });
      
      window.location.href = response.data.checkout_url;
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to create gift. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <Card 
        className="bg-gradient-to-br from-amber-500/10 via-orange-500/10 to-rose-500/10 border-amber-500/20 mb-8 cursor-pointer hover:border-amber-500/40 transition-all"
        onClick={() => setIsOpen(true)}
      >
        <CardContent className="p-6">
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 rounded-full bg-gradient-to-br from-amber-500 to-orange-500 flex items-center justify-center">
              <Gift className="h-6 w-6 text-white" />
            </div>
            <div className="flex-1">
              <h3 className="font-bold text-lg text-primary">Gift a Subscription</h3>
              <p className="text-sm text-muted-foreground">Share the gift of transformation with someone you love</p>
            </div>
            <Button variant="outline" className="border-amber-500/30 text-amber-600 hover:bg-amber-500/10">
              Give a Gift
            </Button>
          </div>
        </CardContent>
      </Card>

      <Dialog open={isOpen} onOpenChange={setIsOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Gift className="h-5 w-5 text-amber-500" />
              Gift a Rooted Subscription
            </DialogTitle>
            <DialogDescription>
              Give someone you care about the gift of spiritual transformation.
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <label className="text-sm font-medium">Recipient's Email *</label>
              <Input
                type="email"
                placeholder="friend@example.com"
                value={giftData.recipient_email}
                onChange={(e) => setGiftData({ ...giftData, recipient_email: e.target.value })}
              />
            </div>
            
            <div className="space-y-2">
              <label className="text-sm font-medium">Recipient's Name</label>
              <Input
                type="text"
                placeholder="Their name (for the gift message)"
                value={giftData.recipient_name}
                onChange={(e) => setGiftData({ ...giftData, recipient_name: e.target.value })}
              />
            </div>
            
            <div className="space-y-2">
              <label className="text-sm font-medium">Gift Duration</label>
              <div className="grid grid-cols-2 gap-2">
                {Object.entries(giftPricing).map(([months, info]) => (
                  <Button
                    key={months}
                    variant={giftData.months === parseInt(months) ? "default" : "outline"}
                    size="sm"
                    onClick={() => setGiftData({ ...giftData, months: parseInt(months) })}
                    className="flex flex-col items-center py-3 h-auto"
                  >
                    <span className="font-semibold">{info.label}</span>
                    <span className="text-xs">${info.price}</span>
                    {info.savings && <span className="text-xs text-green-500">Save {info.savings}</span>}
                  </Button>
                ))}
              </div>
            </div>
            
            <div className="space-y-2">
              <label className="text-sm font-medium">Personal Message (optional)</label>
              <Textarea
                placeholder="Write a heartfelt message to include with your gift..."
                value={giftData.message}
                onChange={(e) => setGiftData({ ...giftData, message: e.target.value })}
                rows={3}
              />
            </div>
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setIsOpen(false)}>
              Cancel
            </Button>
            <Button 
              onClick={handleGift} 
              disabled={loading || !giftData.recipient_email}
              className="bg-gradient-to-r from-amber-500 to-orange-500 hover:from-amber-600 hover:to-orange-600"
            >
              {loading ? "Processing..." : `Gift $${giftPricing[giftData.months].price}`}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
};

// Hardship Application Component
const HardshipApplication = ({ user, getAuthHeader }) => {
  const navigate = useNavigate();
  const [isOpen, setIsOpen] = useState(false);
  const [formData, setFormData] = useState({
    reason: "",
    situation: ""
  });
  const [loading, setLoading] = useState(false);
  const [applied, setApplied] = useState(false);

  const reasons = [
    "Student",
    "Currently unemployed",
    "Single parent",
    "On disability/assistance",
    "Going through hardship",
    "Other"
  ];

  const handleApply = async () => {
    if (!user) {
      toast.info("Let's create your account first!");
      navigate('/register?redirect=/subscription');
      return;
    }
    
    if (!formData.reason) {
      toast.error("Please select a reason");
      return;
    }
    
    setLoading(true);
    try {
      await axios.post(`${API}/subscription/hardship-apply`, formData, {
        headers: getAuthHeader()
      });
      
      toast.success("Application submitted! You now have access to the $2.22/month rate. 💜");
      setApplied(true);
      setIsOpen(false);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to submit. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <button
        onClick={() => setIsOpen(true)}
        className="text-sm text-muted-foreground hover:text-primary underline"
      >
        Need a reduced rate? Apply for hardship pricing ($2.22/mo)
      </button>

      <Dialog open={isOpen} onOpenChange={setIsOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Heart className="h-5 w-5 text-rose-500" />
              Hardship Pricing Application
            </DialogTitle>
            <DialogDescription>
              We believe everyone deserves access to healing tools. This is an honor-system application.
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            <div className="p-4 bg-rose-500/10 rounded-lg border border-rose-500/20">
              <p className="text-sm text-muted-foreground">
                <strong className="text-primary">No verification required.</strong> We trust you. 
                If you're going through a difficult time, we want to support you.
              </p>
            </div>
            
            <div className="space-y-2">
              <label className="text-sm font-medium">What best describes your situation?</label>
              <div className="grid grid-cols-2 gap-2">
                {reasons.map((reason) => (
                  <Button
                    key={reason}
                    variant={formData.reason === reason ? "default" : "outline"}
                    size="sm"
                    onClick={() => setFormData({ ...formData, reason })}
                    className="text-xs"
                  >
                    {reason}
                  </Button>
                ))}
              </div>
            </div>
            
            <div className="space-y-2">
              <label className="text-sm font-medium">Anything you'd like to share? (optional)</label>
              <Textarea
                placeholder="This is completely optional. We're here for you. 💜"
                value={formData.situation}
                onChange={(e) => setFormData({ ...formData, situation: e.target.value })}
                rows={2}
              />
            </div>
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setIsOpen(false)}>
              Cancel
            </Button>
            <Button 
              onClick={handleApply} 
              disabled={loading || !formData.reason}
              className="bg-rose-500 hover:bg-rose-600"
            >
              {loading ? "Submitting..." : "Apply for $2.22/month"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
};

// Tier images for subscription cards - Universal nature-inspired imagery representing "rooted" wellness
const TIER_IMAGES = {
  seedling: "https://images.unsplash.com/photo-1773586075425-bb04804b928b?crop=entropy&cs=srgb&fm=jpg&q=85", // Sunlit forest path - new beginnings
  rooted: "https://images.unsplash.com/photo-1624350324876-43ca097e7165?crop=entropy&cs=srgb&fm=jpg&q=85", // Golden sunrise with trees - growth
  lifetime: "https://images.unsplash.com/photo-1711522645056-8481b56c321b?crop=entropy&cs=srgb&fm=jpg&q=85" // Golden sun rays through forest - eternal light
};

// Promo Code Redemption Component
const PromoCodeSection = ({ user, getAuthHeader, onRedeemed }) => {
  const navigate = useNavigate();
  const [promoCode, setPromoCode] = useState("");
  const [loading, setLoading] = useState(false);
  const [promoStatus, setPromoStatus] = useState(null);

  useEffect(() => {
    const fetchPromoStatus = async () => {
      if (!user) return;
      try {
        const response = await axios.get(`${API}/promo/status`, {
          headers: getAuthHeader()
        });
        setPromoStatus(response.data);
      } catch (error) {
        console.error("Error fetching promo status:", error);
      }
    };
    fetchPromoStatus();
  }, [user, getAuthHeader]);

  const handleRedeem = async () => {
    if (!user) {
      toast.info("Let's create your account first!");
      navigate(`/register?redirect=/subscription&promo=${promoCode}`);
      return;
    }
    
    if (!promoCode.trim()) {
      toast.error("Please enter a promo code");
      return;
    }

    setLoading(true);
    try {
      const response = await axios.post(`${API}/promo/redeem`, 
        { code: promoCode },
        { headers: getAuthHeader() }
      );
      
      toast.success(response.data.message);
      setPromoStatus({ has_promo: true, ...response.data });
      setPromoCode("");
      if (onRedeemed) onRedeemed();
      
      // Refresh page to update user state
      window.location.reload();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Invalid promo code");
    } finally {
      setLoading(false);
    }
  };

  // If user already has promo, show status
  if (promoStatus?.has_promo) {
    // Handle discount type promo codes
    if (promoStatus.type === "discount") {
      return (
        <Card className="bg-gradient-to-r from-emerald-500/10 via-teal-500/10 to-cyan-500/10 border-emerald-500/30 mb-8">
          <CardContent className="p-6">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-12 h-12 rounded-full bg-gradient-to-br from-emerald-500 to-teal-500 flex items-center justify-center">
                <Percent className="h-6 w-6 text-white" />
              </div>
              <div>
                <h3 className="font-bold text-lg text-primary">Discount Code Active!</h3>
                <p className="text-sm text-muted-foreground">Code: {promoStatus.promo_code}</p>
              </div>
            </div>
            
            <div className="bg-emerald-500/10 border border-emerald-500/30 rounded-xl p-4">
              <p className="text-2xl font-bold text-emerald-600 dark:text-emerald-400 mb-1">
                {promoStatus.discount_percent}% OFF
              </p>
              <p className="text-sm text-muted-foreground">
                Your first month on any tier! Choose a plan below.
              </p>
            </div>
          </CardContent>
        </Card>
      );
    }
    
    // Handle promoter type promo codes
    return (
      <Card className="bg-gradient-to-r from-violet-500/10 via-purple-500/10 to-fuchsia-500/10 border-violet-500/30 mb-8">
        <CardContent className="p-6">
          <div className="flex items-center gap-3 mb-4">
            <div className="w-12 h-12 rounded-full bg-gradient-to-br from-violet-500 to-purple-600 flex items-center justify-center">
              <PartyPopper className="h-6 w-6 text-white" />
            </div>
            <div>
              <h3 className="font-bold text-lg text-primary">Promoter Status Active</h3>
              <p className="text-sm text-muted-foreground">Code: {promoStatus.promo_code}</p>
            </div>
          </div>
          
          {promoStatus.is_expired ? (
            <div className="bg-amber-500/10 border border-amber-500/30 rounded-xl p-4">
              <p className="text-sm text-amber-600 dark:text-amber-400 mb-2">
                Your free year has ended! Continue your journey at the exclusive promoter rate.
              </p>
              <p className="text-2xl font-bold text-primary">${promoStatus.discount_rate}/month</p>
              <p className="text-xs text-muted-foreground">for Transcend tier (normally $44.44)</p>
            </div>
          ) : (
            <div className="bg-emerald-500/10 border border-emerald-500/30 rounded-xl p-4">
              <p className="text-sm text-emerald-600 dark:text-emerald-400 mb-1">
                {promoStatus.days_remaining} days remaining of free Transcend access
              </p>
              <p className="text-xs text-muted-foreground">
                After your free year, continue at ${promoStatus.discount_rate}/month
              </p>
            </div>
          )}
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className="bg-gradient-to-r from-amber-500/10 via-orange-500/10 to-rose-500/10 border-amber-500/30 mb-8">
      <CardContent className="p-6">
        <div className="flex items-center gap-3 mb-4">
          <div className="w-12 h-12 rounded-full bg-gradient-to-br from-amber-500 to-orange-500 flex items-center justify-center">
            <Ticket className="h-6 w-6 text-white" />
          </div>
          <div>
            <h3 className="font-bold text-lg text-primary">Have a Promo Code?</h3>
            <p className="text-sm text-muted-foreground">Get discounts or free access!</p>
          </div>
        </div>
        
        <div className="flex flex-col sm:flex-row gap-3">
          <Input
            type="text"
            placeholder="Enter promo code..."
            value={promoCode}
            onChange={(e) => setPromoCode(e.target.value.toUpperCase())}
            className="flex-1 uppercase"
            data-testid="promo-code-input"
          />
          <Button
            onClick={handleRedeem}
            disabled={loading}
            className="bg-gradient-to-r from-amber-500 to-orange-500 hover:from-amber-600 hover:to-orange-600 text-white"
            data-testid="redeem-promo-btn"
          >
            {loading ? "Redeeming..." : "Redeem Code"}
          </Button>
        </div>
        
        <div className="mt-4 p-3 bg-muted/30 rounded-lg">
          <p className="text-xs text-muted-foreground text-center">
            <span className="font-semibold text-emerald-600">WELCOME50</span> - 50% off your first month<br/>
            <span className="font-semibold text-amber-600">Promoter codes</span> - 1 year FREE + $5.55/mo after
          </p>
        </div>
      </CardContent>
    </Card>
  );
};

const Subscription = () => {
  const { user, getAuthHeader } = useAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(null);
  const [promoData, setPromoData] = useState(null);
  const [userPromoStatus, setUserPromoStatus] = useState(null);
  const [showMatrixPreview, setShowMatrixPreview] = useState(false);
  const [previewTier, setPreviewTier] = useState("rooted");

  // Handler to preview the Matrix experience for a tier
  const handlePreviewExperience = (tierId) => {
    // Map tier IDs to MatrixUnlock tier props
    const matrixTierMap = {
      seedling: "seedling",
      rooted: "rooted",
      lifetime: "forever_rooted"
    };
    setPreviewTier(matrixTierMap[tierId] || "rooted");
    setShowMatrixPreview(true);
  };

  // Fetch promotional pricing based on user's journey progress
  useEffect(() => {
    const fetchPromo = async () => {
      if (!user) return;
      try {
        const response = await axios.get(`${API}/promo/pricing`, {
          headers: getAuthHeader()
        });
        setPromoData(response.data);
      } catch (error) {
        console.error("Failed to fetch promo pricing:", error);
      }
    };
    fetchPromo();
  }, [user, getAuthHeader]);

  // Fetch user's promo code status
  useEffect(() => {
    const fetchPromoStatus = async () => {
      if (!user) return;
      try {
        const response = await axios.get(`${API}/promo/status`, {
          headers: getAuthHeader()
        });
        setUserPromoStatus(response.data);
      } catch (error) {
        console.error("Failed to fetch promo status:", error);
      }
    };
    fetchPromoStatus();
  }, [user, getAuthHeader]);

  const getPromoForTier = (tier) => {
    if (!promoData?.pricing) return null;
    return promoData.pricing.find(p => p.tier === tier && p.has_discount);
  };

  // Check if user has a discount code applied
  const hasDiscountCode = userPromoStatus?.has_promo && userPromoStatus?.type === "discount";
  const discountPercent = userPromoStatus?.discount_percent || 0;

  const handleSubscribe = async (tier) => {
    // If user is not logged in, redirect to register with selected tier
    if (!user) {
      toast.info("Let's create your account first!");
      navigate(`/register?redirect=/subscription&tier=${tier}`);
      return;
    }
    
    setLoading(tier);
    
    // Check if user has promo pricing from journey progress
    const promo = getPromoForTier(tier);
    
    try {
      // Use promo checkout if user has earned discount from journey
      const endpoint = promo ? `${API}/subscription/promo-checkout?tier=${tier}` : `${API}/subscription/checkout`;
      const payload = promo ? {} : { origin_url: window.location.origin, tier };
      
      const response = await axios.post(
        endpoint,
        payload,
        { headers: getAuthHeader() }
      );
      
      // Show toast for discounts
      if (promo) {
        toast.success(`${promo.discount_percent}% journey discount applied!`);
      } else if (response.data.discount_applied) {
        toast.success(`${response.data.discount_percent}% promo code discount applied!`);
      }
      
      window.location.href = response.data.checkout_url || response.data.url;
    } catch (error) {
      toast.error("Failed to start checkout. Please try again.");
      setLoading(null);
    }
  };

  const tiers = [
    {
      id: "seedling",
      name: "Seedling",
      price: 0,
      angel: "FREE",
      tagline: "Start your spiritual journey",
      description: "Everything you need to begin. No credit card required.",
      color: "from-green-500/20 to-emerald-500/20",
      borderColor: "border-green-500/30 hover:border-green-500/60",
      accentColor: "text-green-500",
      buttonColor: "bg-green-600 hover:bg-green-700",
      isFree: true,
      features: [
        { icon: Users, text: "Soul Circle Community access" },
        { icon: Sparkles, text: "Daily Affirmations & Vibe Check" },
        { icon: BookOpen, text: "Journal (10 entries / month)" },
        { icon: Music, text: "3 Sound Sanctuary tracks" },
        { icon: Star, text: "Spiritual Awakenings starter" },
        { icon: Heart, text: "And more to explore..." },
      ],
      teaser: "A generous free tier to start your spiritual awakening."
    },
    {
      id: "rooted",
      name: "Rooted",
      price: 5.55,
      yearlyPrice: 44.44,
      angel: "555",
      tagline: "Complete transformation toolkit",
      description: "Everything you need for mind, body, and soul wellness.",
      color: "from-violet-500/20 to-purple-500/20",
      borderColor: "border-violet-500/30 hover:border-violet-500/60",
      accentColor: "text-violet-500",
      buttonColor: "bg-gradient-to-r from-violet-600 to-purple-600 hover:from-violet-700 hover:to-purple-700",
      popular: true,
      features: [
        { icon: Check, text: "Everything in Seedling, PLUS:" },
        { icon: BookOpen, text: "Full Readables Library + Audio" },
        { icon: Zap, text: "Wellness & Lifestyle (AI meal plans)" },
        { icon: Sparkles, text: "Personalized Meditations" },
        { icon: MessageSquare, text: "The Game — Courageous Conversations" },
        { icon: Music, text: "Full Sound Sanctuary & Vibe Out Hub" },
        { icon: Users, text: "Unlimited Soul Circle Community" },
        { icon: Heart, text: "Rooted Resources + Manifestation Deck" },
        { icon: Star, text: "Deeper Spiritual Awakenings" },
        { icon: Crown, text: "And so much more inside..." },
      ],
      teaser: "Everything you need for complete mind, body, and soul transformation."
    },
    {
      id: "lifetime",
      name: "Forever Rooted",
      price: 111.11,
      isLifetime: true,
      angel: "1111",
      tagline: "Pay once, rooted forever",
      description: "One payment. Lifetime access. All future features included.",
      color: "from-amber-500/20 to-orange-500/20",
      borderColor: "border-amber-500/30 hover:border-amber-500/60",
      accentColor: "text-amber-500",
      buttonColor: "bg-gradient-to-r from-amber-600 to-orange-600 hover:from-amber-700 hover:to-orange-700",
      features: [
        { icon: Check, text: "Everything in Rooted, FOREVER" },
        { icon: Infinity, text: "All Future Features Included" },
        { icon: Crown, text: "Founding Member Badge" },
        { icon: Star, text: "Early Access to New Content" },
        { icon: Heart, text: "Name in App Credits (optional)" },
        { icon: Gift, text: "Exclusive Events & more..." },
      ],
      teaser: "For true believers in the mission. Pay once, stay rooted forever."
    }
  ];

  if (user?.is_premium) {
    const currentTier = tiers.find(t => t.id === user.subscription_tier) || tiers[0];
    return (
      <div className="min-h-screen bg-background">
        <header className="sticky top-0 z-50 bg-background/80 backdrop-blur-md border-b border-border/50">
          <div className="max-w-4xl mx-auto px-6 py-4 flex items-center justify-between">
            <Link to="/dashboard" className="flex items-center gap-2 text-muted-foreground hover:text-primary transition-colors">
              <ArrowLeft className="h-5 w-5" />
              <span className="hidden sm:inline">Back to Dashboard</span>
            </Link>
            <div className="flex items-center gap-2">
              <Crown className="h-5 w-5 text-accent" />
              <span className="text-lg text-primary">Subscription</span>
            </div>
          </div>
        </header>

        <main className="max-w-2xl mx-auto px-6 py-24 text-center">
          <div className="animate-fade-in">
            <div className="w-24 h-24 bg-accent/20 rounded-full flex items-center justify-center mx-auto mb-8">
              <Crown className="h-12 w-12 text-accent" />
            </div>
            <h1 className="text-4xl text-primary mb-4">You're on {currentTier.name}!</h1>
            <p className="text-muted-foreground text-lg mb-8 max-w-md mx-auto">
              Thank you for your support. Enjoy all your {currentTier.name} tier features.
            </p>
            <p className="text-sm text-muted-foreground">
              Subscription active until: {user.subscription_end ? new Date(user.subscription_end).toLocaleDateString() : 'Lifetime'}
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center mt-8">
              <Link to="/dashboard">
                <Button className="rounded-full" data-testid="back-to-dashboard-btn">
                  Back to Dashboard
                </Button>
              </Link>
              <Link to="/subscription/manage">
                <Button variant="outline" className="rounded-full" data-testid="manage-subscription-btn">
                  Manage Subscription
                </Button>
              </Link>
            </div>
          </div>
        </main>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      {/* Matrix Preview Experience */}
      <MatrixUnlock 
        isOpen={showMatrixPreview} 
        onClose={() => setShowMatrixPreview(false)}
        tier={previewTier}
      />

      {/* Header */}
      <header className="sticky top-0 z-50 bg-background/80 backdrop-blur-md border-b border-border/50">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <Link to={user ? "/dashboard" : "/"} className="flex items-center gap-2 text-muted-foreground hover:text-primary transition-colors">
            <ArrowLeft className="h-5 w-5" />
            <span className="hidden sm:inline">{user ? "Back to Dashboard" : "Back to Home"}</span>
          </Link>
          <div className="flex items-center gap-2">
            <Crown className="h-5 w-5 text-accent" />
            <span className="text-lg text-primary">Choose Your Path</span>
          </div>
          {!user && (
            <div className="flex gap-2">
              <Link to="/login">
                <Button variant="ghost" size="sm" className="rounded-full">Sign In</Button>
              </Link>
              <Link to="/register">
                <Button size="sm" className="rounded-full bg-primary text-primary-foreground">Register</Button>
              </Link>
            </div>
          )}
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-6 py-12">
        {/* Hero */}
        <div className="text-center mb-12 animate-fade-in">
          <div className="inline-flex items-center gap-2 bg-accent/10 text-accent px-4 py-2 rounded-full text-sm font-medium mb-6">
            <Crown className="h-4 w-4" />
            For Those Who See Differently
          </div>
          <h1 className="text-4xl md:text-5xl text-primary mb-4">Choose Your Transformation</h1>
          <p className="text-muted-foreground max-w-2xl mx-auto text-lg mb-6">
            Each tier is priced with angel numbers to align with your spiritual journey. 
            Start where you feel called.
          </p>
          
          {/* A Subscription That Gives Back */}
          <div className="max-w-3xl mx-auto p-6 bg-gradient-to-r from-rose-500/10 via-pink-500/10 to-violet-500/10 rounded-2xl border border-rose-500/20 text-center mb-8">
            <div className="flex items-center justify-center gap-2 mb-3">
              <Heart className="h-5 w-5 text-rose-500" />
              <h3 className="text-lg font-bold text-primary">A Subscription That Gives Back</h3>
              <Heart className="h-5 w-5 text-rose-500" />
            </div>
            <p className="text-sm text-muted-foreground mb-3">
              Your subscription does more than transform your life — it transforms others. A portion of every 
              subscription goes directly to supporting <strong className="text-rose-500">immigrant families facing legal fees</strong> and 
              helping them stay together.
            </p>
            <p className="text-xs text-rose-600 dark:text-rose-400 font-medium">
              When you invest in your healing, you're also investing in the healing of families in crisis. 💕
            </p>
          </div>
          
          {/* Different Lens Message */}
          <div className="max-w-3xl mx-auto p-6 bg-violet-500/10 rounded-2xl border border-violet-500/20 text-left mb-8">
            <p className="text-sm text-muted-foreground mb-3">
              <strong className="text-primary">This isn't about religion — it's about seeing clearly.</strong> Lala Land 
              is designed for souls who have been called to dive deeper, who see the world through different eyes, 
              and who are ready to explore beyond what any single tradition can contain.
            </p>
            <p className="text-sm text-primary font-medium mb-3 p-3 bg-primary/10 rounded-lg">
              Spirituality is not something to fear — it is <em>returning back to our true nature</em>. We are spiritual beings 
              having a human experience.
            </p>
            <p className="text-xs text-violet-600 dark:text-violet-400 italic">
              "Once you put on the glasses, they can never be taken off. You can never see things as your 
              tribe says they are, but only as they truly are." — Dick Gregory
            </p>
          </div>
        </div>

        {/* Promo Code Section */}
        <PromoCodeSection user={user} getAuthHeader={getAuthHeader} />

        {/* Gift a Subscription */}
        <GiftSubscription user={user} getAuthHeader={getAuthHeader} />

        {/* Pricing Tiers */}
        <div className="grid md:grid-cols-3 gap-8 mb-16">
          {tiers.map((tier, index) => (
            <Card 
              key={tier.id}
              className={`relative bg-gradient-to-br ${tier.color} ${tier.borderColor} transition-all duration-500 hover:-translate-y-2 hover:shadow-xl animate-fade-in overflow-hidden`}
              style={{ animationDelay: `${index * 0.1}s` }}
              data-testid={`tier-card-${tier.id}`}
            >
              <CardContent className="p-6">
                {/* Tier Image */}
                <div className="relative mb-6 rounded-xl overflow-hidden">
                  <img 
                    src={TIER_IMAGES[tier.id]} 
                    alt={`${tier.name} tier`}
                    className="w-full h-40 object-cover"
                  />
                  <div className="absolute bottom-2 left-2 bg-black/60 backdrop-blur-sm text-white text-xs px-2 py-1 rounded flex items-center gap-1">
                    <Sparkles className="h-3 w-3" />
                    {tier.name}
                  </div>
                  {/* Most Popular badge - positioned inside the image */}
                  {tier.popular && (
                    <div className="absolute top-2 right-2 bg-violet-500 text-white text-xs px-3 py-1 rounded-full font-medium shadow-lg">
                      Most Popular
                    </div>
                  )}
                </div>

                {/* Tier Info */}
                <div className="text-center mb-6">
                  <p className={`text-xs uppercase tracking-[0.2em] ${tier.accentColor} font-semibold mb-1`}>
                    Angel {tier.angel}
                  </p>
                  <h3 className="text-2xl font-bold text-primary mb-1">{tier.name}</h3>
                  <p className="text-sm text-muted-foreground mb-4">{tier.tagline}</p>
                  
                  {/* Show promotional pricing if earned from journey */}
                  {getPromoForTier(tier.id) ? (
                    <div className="relative">
                      <div className="absolute -top-3 left-1/2 -translate-x-1/2 bg-gradient-to-r from-emerald-500 to-teal-500 text-white text-xs px-3 py-1 rounded-full font-medium flex items-center gap-1">
                        <Percent className="h-3 w-3" />
                        {getPromoForTier(tier.id).discount_percent}% OFF First Month!
                      </div>
                      <div className="flex items-baseline justify-center gap-2 mt-3">
                        <span className="text-2xl text-muted-foreground line-through">${tier.price}</span>
                        <span className="text-4xl font-bold text-emerald-500">${getPromoForTier(tier.id).promo_price.toFixed(2)}</span>
                        <span className="text-muted-foreground">/first mo</span>
                      </div>
                      <p className="text-xs text-muted-foreground mt-1">Then ${tier.price}/month</p>
                    </div>
                  ) : hasDiscountCode ? (
                    // Show discount from promo code
                    <div className="relative">
                      <div className="absolute -top-3 left-1/2 -translate-x-1/2 bg-gradient-to-r from-amber-500 to-orange-500 text-white text-xs px-3 py-1 rounded-full font-medium flex items-center gap-1">
                        <Percent className="h-3 w-3" />
                        {discountPercent}% OFF First Month!
                      </div>
                      <div className="flex items-baseline justify-center gap-2 mt-3">
                        <span className="text-2xl text-muted-foreground line-through">${tier.price}</span>
                        <span className="text-4xl font-bold text-amber-500">${(tier.price * (1 - discountPercent / 100)).toFixed(2)}</span>
                        <span className="text-muted-foreground">/first mo</span>
                      </div>
                      <p className="text-xs text-muted-foreground mt-1">Then ${tier.price}/month</p>
                    </div>
                  ) : (
                    <div className="flex items-baseline justify-center gap-1">
                      <span className="text-4xl font-bold text-primary">${tier.price}</span>
                      {tier.isLifetime ? (
                        <span className="text-muted-foreground text-sm">one-time</span>
                      ) : (
                        <span className="text-muted-foreground">/month</span>
                      )}
                    </div>
                  )}
                </div>

                {/* Features List */}
                <ul className="space-y-3 mb-6">
                  {tier.features.map((feature, i) => (
                    <li key={i} className="flex items-center gap-3 text-sm">
                      <feature.icon className={`h-4 w-4 ${tier.accentColor} flex-shrink-0`} />
                      <span className="text-foreground">{feature.text}</span>
                    </li>
                  ))}
                </ul>

                {/* Subscribe Button */}
                <Button
                  onClick={() => handleSubscribe(tier.id)}
                  disabled={loading === tier.id}
                  className={`w-full ${tier.buttonColor} text-white rounded-full py-6 transition-all`}
                  data-testid={`subscribe-${tier.id}-btn`}
                >
                  {loading === tier.id ? (
                    "Starting checkout..."
                  ) : (
                    <>Start {tier.name} Journey</>
                  )}
                </Button>

                {/* Preview Experience Button */}
                <Button
                  onClick={() => handlePreviewExperience(tier.id)}
                  variant="ghost"
                  className={`w-full mt-2 rounded-full py-2 text-xs ${tier.accentColor} hover:bg-muted/50 transition-all`}
                  data-testid={`preview-${tier.id}-btn`}
                >
                  <Play className="h-3 w-3 mr-1" />
                  Preview Matrix Experience
                </Button>

                <p className="text-xs text-center text-muted-foreground mt-3">
                  {tier.isLifetime ? "One-time payment • " : "Cancel anytime • "}Secure payment via Stripe
                </p>
              </CardContent>
            </Card>
          ))}
        </div>

        {/* Hardship Pricing Link */}
        <div className="text-center mb-16">
          <HardshipApplication user={user} getAuthHeader={getAuthHeader} />
        </div>

        {/* Pay What You Can - Alternative Option */}
        <PayWhatYouCan user={user} getAuthHeader={getAuthHeader} />

        {/* Community Milestones */}
        <CommunityMilestones />

        {/* Mission & Impact Statement */}
        <div className="text-center animate-fade-in mb-16">
          <Card className="bg-gradient-to-br from-amber-500/10 via-rose-500/10 to-violet-500/10 border-amber-500/30 max-w-3xl mx-auto">
            <CardContent className="p-8">
              <div className="flex justify-center gap-2 mb-4">
                <Heart className="h-6 w-6 text-rose-500" />
                <span className="text-2xl">🌍</span>
                <Heart className="h-6 w-6 text-rose-500" />
              </div>
              <h3 className="text-xl font-semibold text-primary mb-4">Your Membership Makes a Difference</h3>
              <p className="text-muted-foreground mb-4 leading-relaxed">
                A portion of every membership goes directly toward causes we believe in deeply:
              </p>
              <div className="grid md:grid-cols-2 gap-4 mb-6">
                <div className="p-4 rounded-xl bg-amber-500/10 border border-amber-500/20">
                  <span className="text-2xl mb-2 block">🎪</span>
                  <p className="font-semibold text-primary mb-1">Community Events</p>
                  <p className="text-sm text-muted-foreground">Supporting our upcoming festivals, wellness gatherings, and community celebrations</p>
                </div>
                <div className="p-4 rounded-xl bg-rose-500/10 border border-rose-500/20">
                  <span className="text-2xl mb-2 block">⚖️</span>
                  <p className="font-semibold text-primary mb-1">Immigrant Family Defense</p>
                  <p className="text-sm text-muted-foreground">Legal fees for immigrants and their families fighting deportation cases</p>
                </div>
              </div>
              <div className="p-4 rounded-xl bg-violet-500/10 border border-violet-500/20">
                <p className="text-primary font-medium italic">
                  "We are in this fight together. We believe in keeping families united and communities strong. 
                  With every membership, you're not just investing in your wellness — you're helping us keep families together, 
                  one membership at a time."
                </p>
              </div>
              <p className="text-xs text-muted-foreground mt-4">
                🙏🏾 Thank you for being part of our mission to heal, uplift, and protect our communities.
              </p>
            </CardContent>
          </Card>
        </div>

        {/* Angel Number Meaning */}
        <div className="text-center animate-fade-in mb-16 px-4">
          <Card className="bg-muted/30 border-border/50 max-w-2xl mx-auto">
            <CardContent className="p-4 sm:p-8">
              <Star className="h-8 w-8 text-accent mx-auto mb-4" />
              <h3 className="text-lg sm:text-xl font-semibold text-primary mb-4">Why Angel Number Pricing?</h3>
              <p className="text-sm sm:text-base text-muted-foreground mb-6">
                Our prices align with powerful angel numbers, each carrying divine meaning:
              </p>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 sm:gap-4 text-sm">
                <div className="p-3 rounded-lg bg-green-500/10">
                  <p className="font-bold text-green-500 mb-1">FREE</p>
                  <p className="text-muted-foreground text-xs sm:text-sm">Seedling Energy</p>
                </div>
                <div className="p-3 rounded-lg bg-violet-500/10">
                  <p className="font-bold text-violet-500 mb-1">555</p>
                  <p className="text-muted-foreground text-xs sm:text-sm">Transformation & Change</p>
                </div>
                <div className="p-3 rounded-lg bg-amber-500/10">
                  <p className="font-bold text-amber-500 mb-1">1111</p>
                  <p className="text-muted-foreground text-xs sm:text-sm">New Beginnings & Manifestation</p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Comparison Table */}
        <div className="animate-fade-in px-2 sm:px-4">
          <h2 className="text-xl sm:text-2xl text-primary text-center mb-6 sm:mb-8">Compare All Features</h2>
          
          {/* Mobile-friendly scrollable hint */}
          <p className="text-xs text-muted-foreground text-center mb-2 sm:hidden">← Scroll to compare →</p>
          
          <div className="overflow-x-auto -mx-2 sm:mx-0 pb-4">
            <table className="w-full text-xs sm:text-sm" style={{ minWidth: '480px' }}>
              <thead>
                <tr className="border-b border-border">
                  <th className="text-left py-3 px-2 sm:py-4 sm:px-4 text-muted-foreground font-medium sticky left-0 bg-background z-10" style={{ minWidth: '150px' }}>Feature</th>
                  <th className="text-center py-3 px-2 sm:py-4 sm:px-4 text-green-500 font-medium" style={{ minWidth: '70px' }}>
                    <span className="block text-xs sm:text-sm">Seedling</span>
                    <span className="text-[10px] sm:text-xs opacity-80">FREE</span>
                  </th>
                  <th className="text-center py-3 px-2 sm:py-4 sm:px-4 text-violet-500 font-medium" style={{ minWidth: '70px' }}>
                    <span className="block text-xs sm:text-sm">Rooted</span>
                    <span className="text-[10px] sm:text-xs opacity-80">$5.55/mo</span>
                  </th>
                  <th className="text-center py-3 px-2 sm:py-4 sm:px-4 text-amber-500 font-medium" style={{ minWidth: '70px' }}>
                    <span className="block text-xs sm:text-sm">Forever</span>
                    <span className="text-[10px] sm:text-xs opacity-80">$111.11</span>
                  </th>
                </tr>
              </thead>
              <tbody>
                {[
                  // Top 10 features — order mirrors landing page so the buyer
                  // sees a consistent product story everywhere.
                  { feature: "Soul Circle Community", s: true, r: true, l: true },
                  { feature: "Readables Library + Audio", s: "preview", r: true, l: true, highlight: true },
                  { feature: "Wellness & Lifestyle (AI meal plans)", s: "basic", r: true, l: true, highlight: true },
                  { feature: "Journal & Reflection Prompts", s: "10/mo", r: true, l: true },
                  { feature: "Personalized Meditations", s: "starter", r: true, l: true, highlight: true },
                  { feature: "The Game — Courageous Conversations", s: false, r: true, l: true, highlight: true },
                  { feature: "Sound Sanctuary & Vibe Out Hub", s: "3 tracks", r: true, l: true },
                  { feature: "Daily Affirmations & Vibe Check", s: true, r: true, l: true },
                  { feature: "Rooted Resources + Manifestation Deck", s: false, r: true, l: true },
                  { feature: "Spiritual Awakenings (full library)", s: "starter", r: true, l: true },
                  // Lifetime Perks
                  { feature: "All Future Features Included", s: false, r: false, l: true },
                  { feature: "Founding Member Badge", s: false, r: false, l: true },
                  { feature: "Early Access to New Content", s: false, r: false, l: true },
                  { feature: "Exclusive Member Events & More", s: false, r: false, l: true },
                ].map((row, i) => (
                  <tr key={i} className={`border-b border-border/50 hover:bg-muted/30 transition-colors ${row.highlight ? 'bg-violet-500/5' : ''}`}>
                    <td className={`py-2 sm:py-3 px-2 sm:px-4 text-xs sm:text-sm sticky left-0 bg-background z-10 ${row.highlight ? 'text-primary font-medium' : 'text-foreground'}`}>{row.feature}</td>
                    <td className="py-2 sm:py-3 px-2 sm:px-4 text-center">
                      {row.s === true ? <Check className="h-4 w-4 sm:h-5 sm:w-5 text-green-500 mx-auto" /> : 
                       row.s === false ? <span className="text-muted-foreground text-xs">—</span> : 
                       <span className="text-[10px] sm:text-xs text-green-500 font-medium">{row.s}</span>}
                    </td>
                    <td className="py-2 sm:py-3 px-2 sm:px-4 text-center">
                      {row.r === true ? <Check className="h-4 w-4 sm:h-5 sm:w-5 text-violet-500 mx-auto" /> : 
                       row.r === false ? <span className="text-muted-foreground text-xs">—</span> :
                       <span className="text-[10px] sm:text-xs text-violet-500 font-medium">{row.r}</span>}
                    </td>
                    <td className="py-2 sm:py-3 px-2 sm:px-4 text-center">
                      {row.l === true ? <Check className="h-4 w-4 sm:h-5 sm:w-5 text-amber-500 mx-auto" /> : 
                       row.l === false ? <span className="text-muted-foreground text-xs">—</span> :
                       <span className="text-[10px] sm:text-xs text-amber-500 font-medium">{row.l}</span>}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>

        {/* Trust Section & Legal Disclaimer */}
        <div className="text-center py-12 animate-fade-in">
          <p className="text-sm text-muted-foreground mb-4">
            🔒 Secure payment via Stripe • Cancel anytime
          </p>
          <p className="text-xs text-muted-foreground/70 max-w-2xl mx-auto">
            By subscribing, you agree to our Terms of Service. Subscription fees are non-refundable. 
            You may cancel at any time to prevent future charges. Content and features are provided "as is" 
            and Rooted Wellness LLC makes no guarantees regarding specific results. 
            © {new Date().getFullYear()} Rooted Wellness LLC. All rights reserved.
          </p>
        </div>
      </main>
    </div>
  );
};

export default Subscription;
